/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { DataGrid } from 'src/components/DataGrid/DataGrid';
import Box from '@mui/material/Box';
import { Option } from '@im/ui-lib/SelectOption';
import { GridRowId, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';
import { IM_PRICING_GDE_UI_HYBRID_ENABLED } from 'src/util/constants';
import {
  LoadingOverlay,
  NoRowsOverlay_2,
  SortedAscendingIcon,
  SortedDescendingIcon,
} from 'src/components/DataGrid/DataGridFunctions';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Filters from './Filters';
import FilterList from '@mui/icons-material/FilterList';
import {
  ExceptionDealResponse,
  useGetEDApproveReject,
} from '../hooks/exceptionDeal';
import { useTranslate } from '@im/react-localization';
import DialogComponent from '../components/DealDetailsPage/DialogComponent';
import { GetColumns } from './Columns';
import { useSearchParams } from 'react-router-dom';
import { Pagination } from '@im/ui-lib/Pagination';
import { useGetVendorList } from 'src/hooks/vendor-list';
import AffiliatedEndUserDialog from '../components/AffiliatedEndUserDialog/AffiliatedEndUserDialog';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import AutocompleteSearchBar from 'src/components/AutocompleteSearchBar';

export default function EDListPageTable(
  props: Readonly<{
    businessType?: string | null;
    data: ExceptionDealResponse | undefined;
    dateType: string;
    dateValues:
      | {
          maxuploadDate?: string;
          minuploadDate?: string;
          uploadDate?: string;
        }
      | undefined;
    defaultValue: number;
    filters: string;
    hideControls?: boolean;
    isLoading: boolean;
    pageSetup: {
      page: number;
      pageSize: number;
    };
    role: boolean;
    searchInput: string;
    setDateType: React.Dispatch<React.SetStateAction<string>>;
    setDateValues: React.Dispatch<
      React.SetStateAction<
        | {
            maxuploadDate?: string;
            minuploadDate?: string;
            uploadDate?: string;
          }
        | undefined
      >
    >;
    setDefaultValue: React.Dispatch<React.SetStateAction<number>>;
    setDropdownSearch: React.Dispatch<
      React.SetStateAction<
        | {
            businessType?: string;
            dealNumber?: string;
            enduserName?: string;
            programName?: string;
            resellerName?: string;
            vendorName?: string;
          }
        | undefined
      >
    >;
    setFilters: React.Dispatch<React.SetStateAction<string>>;
    setPageSetup: React.Dispatch<
      React.SetStateAction<{
        page: number;
        pageSize: number;
      }>
    >;
    setSearchInput: React.Dispatch<React.SetStateAction<string>>;
    setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
    setVendors: React.Dispatch<React.SetStateAction<string>>;
    sortModel: GridSortModel;
    user: string;
    vendors: string;
  }>
) {
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get('searchValue');
  const dropDownOption = searchParams.get('dropDownOption');
  const role = props.role;
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [openAppRejDia, setOpenAppRejDia] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const vendorList = useGetVendorList();
  const didInitFromUrl = useRef(false);
  const isHybridEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_HYBRID_ENABLED
  );

  const dropDownOptions: Option[] = [
    'Deal #',
    'End user name',
    'Program name',
    'Reseller name',
    'Vendor name',
    ...(isHybridEnabled ? ['BusinessType'] : []),
  ].map((value) => ({
    label: translate(
      `ExceptionDeals.SearchFields${upperFirst(camelCase(value))}`
    ),
    value,
  }));
  const [phText, setPhText] = useState<string>(dropDownOptions[0].value);
  const handleOnSearch = (criteria: any) => {
    if (criteria.inputValue) {
      switch (criteria.dropdownTerm) {
        case 'Vendor name':
          props.setDropdownSearch({ vendorName: criteria.inputValue.trim() });
          break;
        case 'Program name':
          props.setDropdownSearch({ programName: criteria.inputValue.trim() });
          break;
        case 'Reseller name':
          props.setDropdownSearch({ resellerName: criteria.inputValue.trim() });
          break;
        case 'End user name':
          props.setDropdownSearch({ enduserName: criteria.inputValue.trim() });
          break;
        case 'BusinessType':
          props.setDropdownSearch({ businessType: criteria.inputValue.trim() });
          break;
        default:
          props.setDropdownSearch({ dealNumber: criteria.inputValue.trim() });
          break;
      }
      props.setPageSetup({ page: 1, pageSize: 10 });
      searchParams.set('searchValue', criteria.inputValue.trim());
      searchParams.set('dropDownOption', criteria.dropdownTerm);
      setSearchParams(searchParams, { replace: true });
    }
  };
  const handleClearDropDown = (criteria: any) => {
    if (criteria.inputValue) {
      props.setDropdownSearch(undefined);
      props.setSearchInput('');
    }
    searchParams.delete('searchValue');
    searchParams.delete('dropDownOption');
    setSearchParams(searchParams, { replace: true });
  };

  useEffect(() => {
    setPhText(dropDownOptions[props.defaultValue].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultValue]);

  useEffect(() => {
    if (didInitFromUrl.current) return;
    if (!searchValue || !dropDownOption) return;
    const optionIndex = dropDownOptions.findIndex(
      (option) => option.value === dropDownOption
    );
    if (optionIndex === -1) return;
    didInitFromUrl.current = true;
    props.setDefaultValue(optionIndex);
    setPhText(dropDownOption);
    props.setSearchInput(searchValue);
    handleOnSearch({
      dropdownTerm: dropDownOption,
      inputValue: searchValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, dropDownOption, dropDownOptions]);

  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [importreferenceIds, setImportreferenceIds] = useState<string[]>([]);
  const [dealIds, setDealIds] = useState<string[]>([]);
  const [customerSetIds, setCustomerSetIds] = useState<string[]>([]);
  const [dealNumbers, setDealNumbers] = useState<string[]>([]);
  const [dealVersions, setDealVersions] = useState<string[]>([]);
  const [viewAffiliatedEndUSers, setViewAffiliatedEndUSers] =
    useState<string>();
  const [vendorNames, setVendorNames] = useState<string[]>([]);
  const user = props.user;
  const approveReject = useGetEDApproveReject();
  const requestListPagination = () => (
    <Pagination
      data-testid="gds-vp-ed-list-table-pagination"
      onPageChange={(page) => {
        props.setPageSetup({ ...props.pageSetup, page });
      }}
      onRowsPerPageChange={(pageSize) => {
        props.setPageSetup({ page: 1, pageSize });
      }}
      page={props.pageSetup.page}
      rowsPerPage={props.pageSetup.pageSize}
      totalCount={props.data?.totalcount ?? 0}
    />
  );
  const handleApproveReject = (status: boolean) => {
    approveReject
      .mutateAsync({
        businessType: props.businessType ?? '',
        customersetids: customerSetIds.toString(),
        dealids: dealIds.toString(),
        dealnumbers: dealNumbers.toString(),
        dealversions: dealVersions.toString(),
        importreferenceids: importreferenceIds.toString(),
        isapproved: status,
        notes: notes,
        servicePlanResolved: false,
        userid: user,
        vendornames: vendorNames.toString(),
      })
      .then(() => setSelectedRows([]))
      .catch(() => undefined);
  };
  const columns = GetColumns({
    approveReject,
    businessType: props.businessType,
    role,
    setCustomerSetIds,
    setDealIds,
    setImportreferenceIds,
    setOpenAppRejDia,
    setVendorNames,
    setViewAffiliatedEndUSers,
  });
  const BUSINESS_TYPE_OPTIONS = ['HWSW', 'Hybrid', 'Subscription'];
  const selectedDropdown = dropDownOptions[props.defaultValue]?.value;
  const isBusinessTypeSelected = selectedDropdown === 'BusinessType';

  return (
    <Box>
      {approveReject.isLoading && <CenterLoader overlay={true} />}
      <Stack flexDirection={'row'} justifyContent={'space-between'} mb={2}>
        <Stack>
          {!props.hideControls && (
            <AutocompleteSearchBar
              data-testid="gds-vp-all-upload-queue-searchBar"
              keyOptionSelected={dropDownOptions[props.defaultValue]}
              keyOptions={
                props.businessType === 'Subscription'
                  ? [dropDownOptions[0]]
                  : dropDownOptions
              }
              onClear={() => {
                handleClearDropDown({ inputValue: props.searchInput });
              }}
              onKeyOptionChange={(option) => {
                props.setDefaultValue(dropDownOptions.indexOf(option));
                setPhText(option.value);
                props.setSearchInput('');
                props.setDropdownSearch(undefined);
                searchParams.delete('searchValue');
                searchParams.set('dropDownOption', option.value);
                setSearchParams(searchParams, { replace: true });
              }}
              onSearch={(value) =>
                handleOnSearch({
                  dropdownTerm: selectedDropdown,
                  inputValue: value,
                })
              }
              onValueChange={props.setSearchInput}
              placeholder={translate('ExceptionDeals.SearchPlaceholder', {
                field: translate(
                  `ExceptionDeals.SearchFields${upperFirst(camelCase(phText))}`
                ),
              })}
              sx={{ width: '424px' }}
              value={props.searchInput}
              valueOptions={
                isBusinessTypeSelected ? BUSINESS_TYPE_OPTIONS : undefined
              }
            />
          )}
        </Stack>
        {!props.hideControls && (
          <Stack direction="row" spacing={1}>
            {props.businessType !== 'Subscription' && role && (
              <Button
                data-testid="gds-vp-ed-list-table-reject-selected-btn"
                disabled={!selectedRows.length}
                onClick={() => {
                  setOpenAppRejDia(true);
                }}
                startIcon={<CancelOutlinedIcon />}
                variant="newOutlined"
              >
                {translate('ExceptionDeals.RejectSelected')}
              </Button>
            )}
            {props.businessType !== 'Subscription' && role && (
              <Button
                data-testid="gds-vp-ed-list-table-approve-selected-btn"
                disabled={!selectedRows.length}
                onClick={() => {
                  handleApproveReject(true);
                }}
                startIcon={<CheckCircleOutlineOutlinedIcon />}
                variant="newContained"
              >
                {translate('ExceptionDeals.ApproveSelected')}
              </Button>
            )}
            <Button
              data-testid="gds-vp-ed-list-table-filter-btn"
              onClick={() => setOpenFilter(true)}
              startIcon={
                <FilterList
                  style={{ alignSelf: 'stretch', fontSize: '1.125rem' }}
                />
              }
              variant="newOutlined"
            >
              {translate('Common.Filter')}
            </Button>
          </Stack>
        )}
      </Stack>
      {openFilter && (
        <Filters
          businessType={props.businessType}
          dateType={props.dateType}
          dateValues={props.dateValues}
          filters={props.filters}
          handleClose={() => setOpenFilter(false)}
          role={role}
          setDateType={props.setDateType}
          setDateValues={props.setDateValues}
          setFilters={props.setFilters}
          setPageSetup={props.setPageSetup}
          setVendors={props.setVendors}
          vendorList={vendorList.data}
          vendors={props.vendors}
        />
      )}
      <DataGrid
        autoHeight
        checkboxSelection={role}
        columnBuffer={columns.length}
        columns={columns}
        components={{
          ColumnSortedAscendingIcon: SortedAscendingIcon,
          ColumnSortedDescendingIcon: SortedDescendingIcon,
          LoadingOverlay: LoadingOverlay,
          NoRowsOverlay: () => NoRowsOverlay_2(5),
          Pagination: requestListPagination,
        }}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        getRowHeight={() => 'auto'}
        getRowId={(row: GridRowModel) =>
          `${row.importreferenceid}~${row.dealid}~${row.vendorname}~${row.customerseT_ID}~${row.dealnumber}~${row.versionnumber}`
        }
        hideFooterPagination={
          props.data?.totalcount ? props.data?.totalcount <= 0 : true
        }
        hideFooterSelectedRowCount
        isRowSelectable={(params) => {
          if (
            params.row.status === 'Rejected - edits needed' ||
            params.row.status === 'New - edits needed' ||
            params.row.status === 'New Pending Reseller' ||
            params.row.status === 'New Pending EndUser' ||
            params.row.status === 'Deactivated'
          )
            return false;
          return true;
        }}
        loading={props.isLoading}
        onSelectionModelChange={(selectionModel, details) => {
          setSelectedRows(selectionModel);
          const iIds: string[] = [];
          const dIds: string[] = [];
          const vNames: string[] = [];
          const cIds: string[] = [];
          const dNums: string[] = [];
          const dVers: string[] = [];
          selectionModel.forEach((rowId) => {
            iIds.push(String(rowId).split('~')[0]);
            dIds.push(String(rowId).split('~')[1]);
            vNames.push(String(rowId).split('~')[2]);
            cIds.push(String(rowId).split('~')[3]);
            dNums.push(String(rowId).split('~')[4]);
            dVers.push(String(rowId).split('~')[5]);
          });
          setImportreferenceIds(iIds);
          setDealIds(dIds);
          setVendorNames(vNames);
          setCustomerSetIds(cIds);
          setDealNumbers(dNums);
          setDealVersions(dVers);
        }}
        onSortModelChange={(model) => props.setSortModel(model)}
        rows={props.data?.exceptionbids ?? []}
        selectionModel={selectedRows}
        sortModel={props.sortModel}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        sx={{
          '.MuiDataGrid-cell': {
            outline: 'transparent !important',
            overflow: 'hidden',
            padding: '0 20px',
          },
          '.MuiDataGrid-columnHeader--moving': {
            backgroundColor: 'transparent',
          },
          '.MuiDataGrid-columnHeaderCheckbox': {
            padding: '0px',
          },
          '.MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
          },
          '.MuiDataGrid-footerContainer>div': {
            flexWrap: 'wrap',
          },
          '.MuiDataGrid-footerContainer>div>div': {
            flex: 1,
          },
          '.MuiDataGrid-row': {
            minHeight: '52px !important',
          },
          '.MuiSvgIcon-root': {
            color: 'inherit !important',
          },
        }}
      />
      {openAppRejDia && (
        <DialogComponent
          data-testid="gds-vp-ed-list-table-reject-test-dialog"
          handleClose={() => setOpenAppRejDia(false)}
          message={translate('ExceptionDeals.AddReviewNotes')}
          notes={notes}
          onSubmit={() => handleApproveReject(false)}
          requiredNotes={false}
          setNotes={setNotes}
          title={
            importreferenceIds.length > 1
              ? translate('ExceptionDeals.RejectDeals')
              : translate('ExceptionDeals.RejectDeal')
          }
        />
      )}
      {viewAffiliatedEndUSers && (
        <AffiliatedEndUserDialog
          data-testid="gds-vp-ed-list-affliated-user"
          enduser={viewAffiliatedEndUSers ?? ''}
          handleClose={() => setViewAffiliatedEndUSers(undefined)}
          isAffiliated={true}
        />
      )}
    </Box>
  );
}
